import { useCallback, useEffect, useState } from "react";
import { api, formatApiError, fmtDate, fmtDateTime } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import PasteSmsDialog from "@/components/PasteSmsDialog";
import {
    Wand2, Mail, RefreshCcw, Sparkles, CheckCircle2, XCircle, ArrowRight,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const KIND_META = {
    asset: { label: "Asset", tone: "bg-stone-100 text-stone-800 border-stone-200" },
    renewal: { label: "Renewal", tone: "bg-amber-50 text-amber-800 border-amber-200" },
    timeline: { label: "Milestone", tone: "bg-violet-50 text-violet-700 border-violet-200" },
    vehicle: { label: "Vehicle", tone: "bg-sky-50 text-sky-700 border-sky-200" },
    home_project: { label: "Home", tone: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};

export default function SmartCaptureInbox() {
    const { hasRole } = useAuth();
    const canReview = hasRole && hasRole("admin", "finance");
    const [rows, setRows] = useState([]);
    const [cardsList, setCardsList] = useState([]);
    const [busyScan, setBusyScan] = useState(false);
    const [days, setDays] = useState(14);
    const [openRow, setOpenRow] = useState(null); // per-row expanded editing

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/smart-capture/pending");
            setRows(data || []);
        } catch (err) { toast.error(formatApiError(err)); }
    }, []);
    useEffect(() => {
        load();
        api.get("/cards").then((r) => setCardsList((r.data || []).filter((c) => c.status === "Active"))).catch(() => {});
    }, [load]);

    const scan = async () => {
        setBusyScan(true);
        try {
            const { data } = await api.post("/smart-capture/gmail-scan", { days: Number(days) });
            toast.success(`Scanned ${data.messages_scanned} emails · ${data.queued_for_review} new to review · ${data.duplicates_skipped} duplicates skipped.`);
            load();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusyScan(false); }
    };

    const toggleEntity = (rowId, idx) => {
        setRows((old) => old.map((r) => {
            if (r.id !== rowId) return r;
            const entities = (r.parsed?.entities || []).map((e, i) =>
                i === idx ? { ...e, enabled: e.enabled === false ? true : !e.enabled } : e,
            );
            return { ...r, parsed: { ...r.parsed, entities } };
        }));
    };

    const accept = async (row) => {
        try {
            await api.post(`/smart-capture/pending/${row.id}/accept`, {
                entities: row.parsed?.entities || [],
            });
            toast.success("Saved. Entities added to VAULT.");
            load();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const reject = async (row) => {
        try {
            await api.post(`/smart-capture/pending/${row.id}/reject`);
            toast.info("Suggestion rejected.");
            load();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    return (
        <div data-testid="smart-inbox-page">
            <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
                <div>
                    <div className="eyebrow flex items-center gap-1.5"><Wand2 size={12} /> Smart Capture Inbox</div>
                    <h1 className="font-display text-4xl sm:text-5xl text-stone-900 leading-none mt-2">
                        Auto-captured suggestions.
                    </h1>
                    <p className="text-sm text-stone-500 mt-3 max-w-2xl">
                        Order confirmations, warranty emails, booking receipts and other messages that
                        Connected Intelligence turned into ready-to-save entities. Review, edit, accept.
                    </p>
                </div>
                {canReview && (
                    <div className="flex items-center gap-2 flex-wrap">
                        <PasteSmsDialog cards={cardsList} onPosted={load} triggerLabel="Paste bank SMS" />
                        <div className="text-[11px] uppercase tracking-widest text-stone-500">Scan</div>
                        <Input
                            type="number"
                            value={days}
                            onChange={(e) => setDays(e.target.value)}
                            min={1} max={90}
                            className="w-20 font-mono-figure"
                            data-testid="smart-inbox-days"
                        />
                        <div className="text-[12px] text-stone-500 mr-2">days</div>
                        <Button onClick={scan} disabled={busyScan} className="bg-stone-900 hover:bg-stone-800 text-white" data-testid="smart-inbox-scan-btn">
                            <RefreshCcw size={13} className={`mr-1.5 ${busyScan ? "animate-spin" : ""}`} />
                            {busyScan ? "Scanning…" : "Scan Gmail"}
                        </Button>
                    </div>
                )}
            </div>

            {rows.length === 0 ? (
                <div className="rounded-lg border border-dashed border-stone-300 bg-white p-14 text-center" data-testid="smart-inbox-empty">
                    <Mail size={20} className="mx-auto mb-3 text-stone-400" />
                    <div className="text-sm text-stone-600">Nothing to review right now.</div>
                    <div className="text-[12px] text-stone-500 mt-2">
                        Connect Gmail and click <span className="text-stone-800">Scan Gmail</span> to pull recent order and warranty emails.
                    </div>
                </div>
            ) : (
                <div className="space-y-3" data-testid="smart-inbox-list">
                    {rows.map((row) => {
                        const parsed = row.parsed || {};
                        const entities = parsed.entities || [];
                        const isOpen = openRow === row.id;
                        return (
                            <div key={row.id} className="rounded-lg border border-stone-200 bg-white p-4" data-testid={`smart-inbox-row-${row.id}`}>
                                <div className="flex items-start gap-3">
                                    <div className="w-9 h-9 rounded-lg bg-violet-50 border border-violet-200 text-violet-800 flex items-center justify-center shrink-0">
                                        <Sparkles size={14} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-start justify-between gap-3 flex-wrap">
                                            <div className="min-w-0">
                                                <div className="text-sm font-medium text-stone-900 truncate">
                                                    {parsed.summary || row.subject || "Order email"}
                                                </div>
                                                <div className="text-[11px] text-stone-500 mt-0.5 truncate">
                                                    {row.sender} · {fmtDateTime(row.received_date || row.created_at)}
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2 shrink-0">
                                                <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-stone-50 text-stone-600 border border-stone-200">
                                                    {parsed.kind || "other"}
                                                </span>
                                                {parsed.confidence != null && (
                                                    <span className="text-[10px] font-mono-figure text-stone-500">
                                                        {Math.round((parsed.confidence || 0) * 100)}%
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        <div className="mt-3 flex flex-wrap gap-1.5">
                                            {entities.map((e, i) => {
                                                const meta = KIND_META[e.kind] || KIND_META.asset;
                                                return (
                                                    <span key={i} className={`text-[11px] px-2 py-0.5 rounded-full border ${meta.tone}`}>
                                                        {meta.label}: {e.label}
                                                    </span>
                                                );
                                            })}
                                            {parsed.expense?.amount && (
                                                <span className="text-[11px] px-2 py-0.5 rounded-full border bg-stone-50 text-stone-700 border-stone-200 font-mono-figure">
                                                    ₹{Number(parsed.expense.amount).toLocaleString("en-IN")}
                                                </span>
                                            )}
                                            {parsed.card_hint?.matched_card && (
                                                <span className="text-[11px] px-2 py-0.5 rounded-full border bg-sky-50 text-sky-700 border-sky-200">
                                                    ••••{parsed.card_hint.matched_card.last_4}
                                                </span>
                                            )}
                                        </div>

                                        <div className="mt-4 flex items-center gap-2 flex-wrap">
                                            {canReview && (
                                                <>
                                                    <Button
                                                        size="sm"
                                                        onClick={() => setOpenRow(isOpen ? null : row.id)}
                                                        variant="outline"
                                                        data-testid={`smart-inbox-edit-${row.id}`}
                                                    >
                                                        {isOpen ? "Hide" : "Edit selection"}
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        onClick={() => accept(row)}
                                                        className="bg-emerald-700 hover:bg-emerald-800 text-white"
                                                        disabled={entities.length === 0}
                                                        data-testid={`smart-inbox-accept-${row.id}`}
                                                    >
                                                        <CheckCircle2 size={12} className="mr-1.5" /> Accept
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        onClick={() => reject(row)}
                                                        variant="outline"
                                                        className="text-red-700 border-red-200 hover:bg-red-50"
                                                        data-testid={`smart-inbox-reject-${row.id}`}
                                                    >
                                                        <XCircle size={12} className="mr-1.5" /> Reject
                                                    </Button>
                                                </>
                                            )}
                                        </div>

                                        {isOpen && entities.length > 0 && (
                                            <div className="mt-3 space-y-1.5 border-t border-stone-100 pt-3" data-testid={`smart-inbox-edit-body-${row.id}`}>
                                                {entities.map((e, i) => {
                                                    const meta = KIND_META[e.kind] || KIND_META.asset;
                                                    const enabled = e.enabled !== false;
                                                    return (
                                                        <label key={i} className={`flex items-start gap-3 rounded-md p-2 cursor-pointer ${enabled ? "bg-stone-50" : "bg-stone-50/50 opacity-60"}`}>
                                                            <Checkbox checked={enabled} onCheckedChange={() => toggleEntity(row.id, i)} data-testid={`smart-inbox-entity-toggle-${row.id}-${i}`} />
                                                            <div className="flex-1 min-w-0">
                                                                <div className={`inline-flex text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded border ${meta.tone}`}>
                                                                    {meta.label}
                                                                </div>
                                                                <div className="text-[13px] text-stone-800 mt-0.5">{e.label}</div>
                                                                {e.payload?.expiry_date && (
                                                                    <div className="text-[11px] text-stone-500">
                                                                        Expiry {fmtDate(e.payload.expiry_date)}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </label>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            <div className="mt-8 text-[12px] text-stone-500">
                Tip: You can also paste any text as a one-off from the <span className="text-stone-800">Life Moments</span> card on the home dashboard <ArrowRight size={11} className="inline" />
            </div>
        </div>
    );
}
